import { useState, useEffect, useCallback, useMemo } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MapPin, Truck, Loader2, Search, AlertTriangle, CheckCircle, Package,
  Eye, Pencil, XCircle, Plus,
} from "lucide-react";
import PickupRequests from "@/pages/customer/PickupRequests";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/v2/pickup-requests`;

const STATUS_BADGE = {
  SCHEDULED: "bg-blue-100 text-blue-700",
  ASSIGNED: "bg-orange-100 text-orange-700",
  BROADCASTED: "bg-purple-100 text-purple-700",
  PICKED: "bg-emerald-100 text-emerald-700",
  PARTIAL: "bg-amber-100 text-amber-700",
  COMPLETED: "bg-emerald-100 text-emerald-700",
  CANCELLED: "bg-red-100 text-red-700",
  CLOSED_UNFULFILLED: "bg-slate-700 text-white",
};
const CLOSE_REASONS = [
  { value: "CUSTOMER_RESCHEDULED", label: "Customer Rescheduled" },
  { value: "NOT_AVAILABLE", label: "Not Available" },
  { value: "NOT_RESPONDING", label: "Not Responding" },
  { value: "ORDER_NOT_READY", label: "Order Not Ready" },
  { value: "NOT_PACKED", label: "Not Packed" },
  { value: "OTHER", label: "Other" },
];
const SLOT_END_HOUR = { MORNING: 13, EVENING: 18, URGENT: 23 };

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function PickupConsole() {
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [view, setView] = useState("list"); // list | area

  // Filters
  const [customerSearch, setCustomerSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [pincodeFilter, setPincodeFilter] = useState("");

  // Details + edit
  const [detail, setDetail] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Mark picked
  const [pickedIds, setPickedIds] = useState(new Set());
  const [closeModalPid, setCloseModalPid] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [closeOtp, setCloseOtp] = useState("");
  const [closeReasonCode, setCloseReasonCode] = useState("");
  const [closeReasonText, setCloseReasonText] = useState("");
  const [closingSaving, setClosingSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Staff view fetches across ALL customers — iterate customer list
      const custRes = await fetch(`${API}/api/users?role=customer`, { headers });
      const custs = custRes.ok ? await custRes.json() : [];
      setCustomers(custs);

      const results = [];
      for (const c of custs) {
        const p = new URLSearchParams();
        p.set("customer_id", c.id);
        if (statusFilter !== "All") p.set("status", statusFilter);
        if (dateFrom) p.set("date_from", dateFrom);
        if (dateTo) p.set("date_to", dateTo);
        const r = await fetch(`${BASE}?${p.toString()}`, { headers });
        if (r.ok) {
          const d = await r.json();
          (d.items || []).forEach((x) => {
            x.customer_name = c.name;
            x.customer_email = c.email;
            results.push(x);
          });
        }
      }
      setItems(results);
    } catch { /* ignore */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, dateFrom, dateTo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (customerSearch && !(i.customer_name || "").toLowerCase().includes(customerSearch.toLowerCase())
          && !(i.customer_email || "").toLowerCase().includes(customerSearch.toLowerCase())) return false;
      if (pincodeFilter) {
        // Need location pincode from location_name/area — fallback check via detail if available
        const pin = i.location_pincode || "";
        if (!String(pin).startsWith(pincodeFilter)) return false;
      }
      return true;
    });
  }, [items, customerSearch, pincodeFilter]);

  // Urgent banner: pickups with slot ending within 60 minutes
  const urgentCount = useMemo(() => {
    const now = new Date();
    return filtered.filter((i) => {
      if (["COMPLETED", "CANCELLED"].includes(i.status)) return false;
      if (i.pickup_date !== todayIso()) return false;
      const endHour = SLOT_END_HOUR[i.pickup_slot] || 18;
      const end = new Date(); end.setHours(endHour, 0, 0, 0);
      const diffMins = (end - now) / 60000;
      return diffMins > 0 && diffMins <= 60;
    }).length;
  }, [filtered]);

  const summary = useMemo(() => {
    const today = todayIso();
    return {
      total_today: filtered.filter((i) => i.pickup_date === today).length,
      broadcasted: filtered.filter((i) => i.status === "BROADCASTED").length,
      partial: filtered.filter((i) => i.status === "PARTIAL").length,
      completed_today: filtered.filter((i) => i.status === "COMPLETED" && i.pickup_date === today).length,
    };
  }, [filtered]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((i) => {
      const pin = i.location_pincode || "unknown";
      const area = String(pin).slice(0, 3);
      if (!g[area]) g[area] = [];
      g[area].push(i);
    });
    return g;
  }, [filtered]);

  const openDetail = async (row) => {
    try {
      const r = await fetch(`${BASE}/${row.pickup_id}`, { headers });
      if (r.ok) {
        const d = await r.json();
        setDetail({ ...d, customer_name: row.customer_name });
        setEditForm({
          pickup_date: d.pickup_date, pickup_slot: d.pickup_slot,
          contact_person: d.contact_person, contact_phone: d.contact_phone,
          notes: d.notes || "",
        });
        setPickedIds(new Set());
        setEditMode(false);
      }
    } catch (e) { toast.error(e.message); }
  };

  const cancelPickup = async (pid) => {
    if (!window.confirm(`Cancel pickup ${pid}?`)) return;
    try {
      const r = await fetch(`${BASE}/${pid}/cancel`, {
        method: "PATCH", headers,
        body: JSON.stringify({ reason: "Staff cancelled", otp_code: "000000" }),
      });
      if (r.ok) { toast.success("Cancelled"); setDetail(null); fetchAll(); }
      else toast.error("Cancel failed");
    } catch (e) { toast.error(e.message); }
  };

  const saveEdit = async () => {
    if (!detail) return;
    try {
      const r = await fetch(`${BASE}/${detail.pickup_id}`, {
        method: "PUT", headers, body: JSON.stringify(editForm),
      });
      if (r.ok) {
        toast.success("Pickup updated");
        setEditMode(false);
        fetchAll();
      } else {
        toast.error("Update not supported — please cancel and recreate");
      }
    } catch (e) { toast.error(e.message); }
  };

  const markPicked = async () => {
    if (!detail) return;
    try {
      const r = await fetch(`${BASE}/${detail.pickup_id}/mark-picked`, {
        method: "PATCH", headers,
        body: JSON.stringify({ picked_shipment_ids: Array.from(pickedIds) }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Marked ${d.picked_count}/${d.expected_count}`);
        setDetail(null);
        fetchAll();
      } else {
        toast.error("Mark picked failed");
      }
    } catch (e) { toast.error(e.message); }
  };

  const openCloseModal = (pid) => {
    setCloseModalPid(pid);
    setOtpSent(false);
    setCloseOtp("");
    setCloseReasonCode("");
    setCloseReasonText("");
  };

  const sendCloseOtp = async () => {
    if (!closeModalPid) return;
    setSendingOtp(true);
    try {
      const r = await fetch(`${BASE}/${closeModalPid}/send-close-otp`, { method: "POST", headers });
      if (r.ok) {
        toast.success("OTP sent to pickup location contact");
        setOtpSent(true);
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || "Failed to send OTP");
      }
    } catch (e) { toast.error(e.message); }
    finally { setSendingOtp(false); }
  };

  const submitClose = async () => {
    if (!closeModalPid) return;
    if (!closeOtp.trim()) return toast.error("Enter the OTP");
    if (!closeReasonCode) return toast.error("Select a reason");
    if (closeReasonCode === "OTHER" && !closeReasonText.trim()) return toast.error("Enter a reason for Other");
    setClosingSaving(true);
    try {
      const r = await fetch(`${BASE}/${closeModalPid}/close-with-reason`, {
        method: "PATCH", headers,
        body: JSON.stringify({ otp: closeOtp.trim(), reason_code: closeReasonCode, reason_text: closeReasonText || undefined }),
      });
      if (r.ok) {
        toast.success("Pickup request closed");
        setCloseModalPid(null);
        setDetail(null);
        fetchAll();
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || "Failed to close pickup");
      }
    } catch (e) { toast.error(e.message); }
    finally { setClosingSaving(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="pickup-console">
        {/* HEADER */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Pickup Console</h1>
            <p className="text-sm text-slate-500">All pickup requests — manage and monitor</p>
          </div>
          <CreatePickupLauncher onCreated={fetchAll} customers={customers} />
        </div>

        {/* ALERT BANNER */}
        {urgentCount > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 flex items-center gap-2"
               data-testid="urgent-banner">
            <AlertTriangle className="w-4 h-4" />
            {urgentCount} pickup{urgentCount > 1 ? "s" : ""} need immediate attention (slot ending within 60 mins)
          </div>
        )}

        {/* SUMMARY */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card icon={Package} label="Total Today" value={summary.total_today} color="text-blue-600" />
          <Card icon={Truck} label="Broadcasted" value={summary.broadcasted} color="text-purple-600" />
          <Card icon={AlertTriangle} label="Partial" value={summary.partial} color="text-amber-600" />
          <Card icon={CheckCircle} label="Completed Today" value={summary.completed_today} color="text-emerald-600" />
        </div>

        {/* VIEW TOGGLE */}
        <div className="flex gap-2">
          <Button variant={view === "list" ? "default" : "outline"} size="sm"
                  onClick={() => setView("list")} data-testid="view-list">List View</Button>
          <Button variant={view === "area" ? "default" : "outline"} size="sm"
                  onClick={() => setView("area")} data-testid="view-area">Grouped by Area</Button>
        </div>

        {/* FILTERS */}
        <div className="bg-white border rounded-lg p-3 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
          <div className="col-span-2">
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Customer</label>
            <Input value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)}
                   className="h-9 text-sm" placeholder="name or email"
                   data-testid="filter-customer" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   className="h-9 text-sm" data-testid="filter-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   className="h-9 text-sm" data-testid="filter-to" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full border border-slate-300 rounded h-9 px-2 text-sm"
                    data-testid="filter-status">
              <option>All</option>
              <option>SCHEDULED</option>
              <option>ASSIGNED</option>
              <option>BROADCASTED</option>
              <option>PARTIAL</option>
              <option>COMPLETED</option>
              <option>CANCELLED</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Pincode</label>
            <Input value={pincodeFilter} onChange={(e) => setPincodeFilter(e.target.value)}
                   className="h-9 text-sm" placeholder="3-6 digits"
                   data-testid="filter-pincode" />
          </div>
        </div>

        {/* LIST VIEW */}
        {view === "list" && (
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-2 py-2">Pickup ID</th>
                    <th className="text-left px-2 py-2">Customer</th>
                    <th className="text-left px-2 py-2">Location</th>
                    <th className="text-left px-2 py-2">Pincode</th>
                    <th className="text-left px-2 py-2">Date</th>
                    <th className="text-left px-2 py-2">Slot</th>
                    <th className="text-left px-2 py-2">Status</th>
                    <th className="text-center px-2 py-2">AWBs</th>
                    <th className="text-right px-2 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} className="py-10 text-center">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                    </td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={9} className="py-10 text-center text-slate-400">No pickup requests</td></tr>
                  ) : filtered.map((r) => (
                    <tr key={r.pickup_id} className="border-b border-slate-100 hover:bg-slate-50/50"
                        data-testid={`row-${r.pickup_id}`}>
                      <td className="px-2 py-2 font-mono text-[11px]">{r.pickup_id}</td>
                      <td className="px-2 py-2">{r.customer_name}</td>
                      <td className="px-2 py-2">{r.location_name || "—"}</td>
                      <td className="px-2 py-2 font-mono text-[11px]">{r.location_pincode || "—"}</td>
                      <td className="px-2 py-2">{r.pickup_date}</td>
                      <td className="px-2 py-2">{r.pickup_slot}</td>
                      <td className="px-2 py-2">
                        <Badge className={`text-[10px] ${STATUS_BADGE[r.status] || "bg-slate-100"}`}>
                          {r.status}
                        </Badge>
                        {r.is_overdue && (
                          <Badge className="text-[10px] bg-red-100 text-red-700 ml-1">OVERDUE</Badge>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center">{r.picked_count || 0}/{r.expected_count || 0}</td>
                      <td className="px-2 py-2 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="outline" size="sm" onClick={() => openDetail(r)}
                                  data-testid={`view-${r.pickup_id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          {r.status === "SCHEDULED" && (
                            <Button variant="outline" size="sm" onClick={() => cancelPickup(r.pickup_id)}
                                    data-testid={`cancel-${r.pickup_id}`}
                                    className="text-red-600 border-red-200">
                              <XCircle className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {r.is_overdue && (r.status === "SCHEDULED" || r.status === "PARTIAL") && (
                            <Button variant="outline" size="sm" onClick={() => openCloseModal(r.pickup_id)}
                                    data-testid={`close-${r.pickup_id}`}
                                    className="text-amber-700 border-amber-300">
                              Close
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* GROUPED VIEW */}
        {view === "area" && (
          <div className="space-y-3">
            {Object.entries(grouped).map(([area, list]) => {
              const totalLrs = list.reduce((s, p) => s + (p.expected_count || 0), 0);
              return (
                <div key={area} className="bg-white border rounded-lg p-4"
                     data-testid={`area-${area}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-bold text-slate-700">
                        Area {area}xxx — {list.length} pickups, {totalLrs} LRs
                      </h3>
                    </div>
                    <Button variant="outline" size="sm"
                            onClick={() => toast.info("Re-broadcast: backend endpoint coming")}
                            data-testid={`rebroadcast-${area}`}>
                      Re-broadcast to Area Partners
                    </Button>
                  </div>
                  <div className="divide-y">
                    {list.map((r) => (
                      <div key={r.pickup_id} className="py-2 flex items-center gap-3 text-xs">
                        <span className="font-mono text-[11px]">{r.pickup_id}</span>
                        <span className="flex-1 text-slate-600">{r.customer_name} · {r.location_name}</span>
                        <span>{r.pickup_date} · {r.pickup_slot}</span>
                        <Badge className={`text-[10px] ${STATUS_BADGE[r.status] || "bg-slate-100"}`}>
                          {r.status}
                        </Badge>
                        <Button variant="outline" size="sm" onClick={() => openDetail(r)}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {Object.keys(grouped).length === 0 && (
              <div className="bg-white border rounded-lg p-10 text-center text-slate-400">No pickup requests</div>
            )}
          </div>
        )}

        {/* DETAIL SIDE PANEL (as modal) */}
        <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Pickup Details — {detail?.pickup_id}</DialogTitle>
            </DialogHeader>
            {detail && (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <D label="Customer" value={detail.customer_name || "—"} />
                  <D label="Status" value={detail.status} />
                  <D label="Location" value={detail.location_name || "—"} />
                  <D label="Pincode" value={detail.location_pincode || "—"} />
                  {!editMode ? (
                    <>
                      <D label="Date" value={detail.pickup_date} />
                      <D label="Slot" value={detail.slot_time || detail.pickup_slot} />
                      <D label="Contact" value={`${detail.contact_person} · ${detail.contact_phone}`} />
                      <D label="Boxes" value={detail.box_count} />
                      <D label="Notes" value={detail.notes || "—"} />
                    </>
                  ) : (
                    <>
                      <div className="col-span-2 grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] uppercase text-slate-500 font-semibold">Date</label>
                          <Input type="date" value={editForm.pickup_date}
                                 onChange={(e) => setEditForm({ ...editForm, pickup_date: e.target.value })}
                                 className="h-9 text-sm" />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase text-slate-500 font-semibold">Slot</label>
                          <select value={editForm.pickup_slot}
                                  onChange={(e) => setEditForm({ ...editForm, pickup_slot: e.target.value })}
                                  className="w-full border border-slate-300 rounded h-9 px-2 text-sm">
                            <option>MORNING</option><option>EVENING</option><option>URGENT</option>
                          </select>
                        </div>
                        <Input value={editForm.contact_person}
                               onChange={(e) => setEditForm({ ...editForm, contact_person: e.target.value })}
                               className="h-9 text-sm" placeholder="Contact person" />
                        <Input value={editForm.contact_phone}
                               onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                               className="h-9 text-sm" placeholder="Contact phone" />
                        <Input className="col-span-2 h-9 text-sm" placeholder="Notes"
                               value={editForm.notes}
                               onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
                      </div>
                    </>
                  )}
                </div>

                <div className="border-t pt-3">
                  <h4 className="text-xs font-bold text-slate-700 uppercase mb-2">Linked Shipments</h4>
                  {!detail.shipments || detail.shipments.length === 0 ? (
                    <p className="text-xs text-slate-400">No linked shipments (blind manifest)</p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-2 py-1 text-center">Pick?</th>
                          <th className="px-2 py-1 text-left">AWB</th>
                          <th className="px-2 py-1 text-left">Destination</th>
                          <th className="px-2 py-1 text-right">Weight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.shipments.map((s) => (
                          <tr key={s.id} className="border-t border-slate-100">
                            <td className="px-2 py-1 text-center">
                              <input type="checkbox"
                                     checked={pickedIds.has(s.id)}
                                     onChange={() => {
                                       const n = new Set(pickedIds);
                                       n.has(s.id) ? n.delete(s.id) : n.add(s.id);
                                       setPickedIds(n);
                                     }} />
                            </td>
                            <td className="px-2 py-1 font-mono text-[11px]">{s.awb || s.tracking_id}</td>
                            <td className="px-2 py-1">{s.destination?.city || "—"}</td>
                            <td className="px-2 py-1 text-right">{s.chargeable_weight || 0} kg</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
            <DialogFooter className="flex-wrap gap-2">
              {detail?.status === "SCHEDULED" && !editMode && (
                <Button variant="outline" onClick={() => setEditMode(true)} data-testid="edit-btn">
                  <Pencil className="w-4 h-4 mr-1.5" /> Edit
                </Button>
              )}
              {editMode && (
                <>
                  <Button variant="outline" onClick={() => setEditMode(false)}>Cancel Edit</Button>
                  <Button onClick={saveEdit} data-testid="save-edit">Save</Button>
                </>
              )}
              {detail?.shipments && detail.shipments.length > 0 && !editMode && (
                <Button onClick={markPicked} className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="mark-picked-btn">
                  <CheckCircle className="w-4 h-4 mr-1.5" /> Mark Picked ({pickedIds.size})
                </Button>
              )}
              {detail?.status === "SCHEDULED" && !editMode && (
                <Button onClick={() => cancelPickup(detail.pickup_id)}
                        className="bg-red-600 hover:bg-red-700" data-testid="cancel-pickup-btn">
                  <XCircle className="w-4 h-4 mr-1.5" /> Cancel
                </Button>
              )}
              {detail?.is_overdue && (detail?.status === "SCHEDULED" || detail?.status === "PARTIAL") && !editMode && (
                <Button onClick={() => openCloseModal(detail.pickup_id)}
                        className="bg-amber-600 hover:bg-amber-700" data-testid="close-pickup-btn">
                  <AlertTriangle className="w-4 h-4 mr-1.5" /> Close with Reason
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!closeModalPid} onOpenChange={(o) => !o && setCloseModalPid(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Close Missed Pickup — {closeModalPid}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                This pickup wasn't collected on its scheduled day. Verify with the pickup location contact via OTP, then select a reason to close it.
              </p>
              {!otpSent ? (
                <Button onClick={sendCloseOtp} disabled={sendingOtp} className="w-full" data-testid="send-close-otp">
                  {sendingOtp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Send OTP to Contact
                </Button>
              ) : (
                <>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">OTP</label>
                    <Input value={closeOtp} onChange={(e) => setCloseOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                           placeholder="6-digit OTP" data-testid="close-otp-input" />
                    <button onClick={sendCloseOtp} disabled={sendingOtp} className="text-xs text-brand-500 hover:underline mt-1">
                      Resend OTP
                    </button>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">Reason</label>
                    <select value={closeReasonCode} onChange={(e) => setCloseReasonCode(e.target.value)}
                            className="w-full border border-slate-300 rounded h-10 px-2 text-sm bg-white"
                            data-testid="close-reason-select">
                      <option value="">— Select —</option>
                      {CLOSE_REASONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  {closeReasonCode === "OTHER" && (
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">Specify Reason</label>
                      <Input value={closeReasonText} onChange={(e) => setCloseReasonText(e.target.value)}
                             placeholder="Type the reason" data-testid="close-reason-text" />
                    </div>
                  )}
                  <Button onClick={submitClose} disabled={closingSaving} className="w-full bg-amber-600 hover:bg-amber-700"
                          data-testid="submit-close-btn">
                    {closingSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Close Pickup Request
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function CreatePickupLauncher({ onCreated, customers }) {
  const [open, setOpen] = useState(false);
  const [bookingFor, setBookingFor] = useState("customer");
  const [partners, setPartners] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [networkType, setNetworkType] = useState("OWN");
  const [locationId, setLocationId] = useState("");
  const [pickupDate, setPickupDate] = useState(todayIso());
  const [pickupSlot, setPickupSlot] = useState("MORNING");
  const [contactPerson, setContactPerson] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [boxCount, setBoxCount] = useState(1);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!open) return;
    if (bookingFor === "partner" && partners.length === 0) {
      fetch(`${API}/api/v2/partners?is_active=true`, { headers })
        .then(r => r.json()).then(d => setPartners(Array.isArray(d) ? d : []))
        .catch(() => setPartners([]));
    }
  }, [open, bookingFor]);

  useEffect(() => {
    if (!selectedId) { setLocations([]); return; }
    const ownerType = bookingFor === "customer" ? "customer" : "partner";
    fetch(`${API}/api/v2/pickup-locations/list?owner_id=${selectedId}&owner_type=${ownerType}`, { headers })
      .then(r => r.json()).then(d => setLocations(Array.isArray(d) ? d : (d.items || [])))
      .catch(() => setLocations([]));
  }, [selectedId, bookingFor]);

  const reset = () => {
    setBookingFor("customer"); setSelectedId(""); setNetworkType("OWN");
    setLocationId(""); setPickupDate(todayIso()); setPickupSlot("MORNING");
    setContactPerson(""); setContactPhone(""); setBoxCount(1);
  };

  const submit = async () => {
    if (!selectedId) return toast.error(`Select a ${bookingFor}`);
    if (!locationId) return toast.error("Select a pickup location");
    if (!contactPerson.trim() || !contactPhone.trim()) return toast.error("Contact details required");
    setSaving(true);
    try {
      const payload = {
        customer_id: bookingFor === "customer" ? selectedId : null,
        partner_id: bookingFor === "partner" ? selectedId : null,
        network_type: networkType,
        pickup_location_id: locationId,
        pickup_date: pickupDate,
        pickup_slot: pickupSlot,
        contact_person: contactPerson,
        contact_phone: contactPhone,
        box_count: Number(boxCount),
        shipment_ids: [],
      };
      const r = await fetch(`${API}/api/v2/pickup-requests`, {
        method: "POST", headers, body: JSON.stringify(payload),
      });
      if (r.ok) {
        toast.success("Pickup request created");
        setOpen(false); reset(); onCreated && onCreated();
      } else {
        const e = await r.json();
        toast.error(e.detail || "Failed to create pickup request");
      }
    } catch (e) {
      toast.error("Error: " + e.message);
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="create-pickup-btn">
        <Plus className="w-4 h-4 mr-1.5" /> Create Pickup Request
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Create Pickup Request</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <button
                onClick={() => { setBookingFor("customer"); setSelectedId(""); }}
                className={`flex-1 py-2 rounded text-sm font-semibold ${bookingFor === "customer" ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-600"}`}
              >Customer</button>
              <button
                onClick={() => { setBookingFor("partner"); setSelectedId(""); }}
                className={`flex-1 py-2 rounded text-sm font-semibold ${bookingFor === "partner" ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-600"}`}
              >Partner</button>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">{bookingFor === "customer" ? "Customer" : "Partner"} *</label>
              <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
                className="w-full border rounded px-2 py-2 text-sm mt-1">
                <option value="">Select...</option>
                {(bookingFor === "customer" ? customers : partners).map(x => (
                  <option key={x.id || x._id} value={x.id || x._id}>{x.name || x.email}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Network Type *</label>
              <select value={networkType} onChange={e => setNetworkType(e.target.value)}
                className="w-full border rounded px-2 py-2 text-sm mt-1">
                <option value="OWN">Own Network</option>
                <option value="3PL_B2C">3PL — Delhivery B2C</option>
                <option value="3PL_B2B">3PL — Delhivery B2B</option>
              </select>
              {networkType === "3PL_B2B" && (
                <p className="text-xs text-indigo-600 bg-indigo-50 px-2 py-1.5 rounded mt-1">
                  B2B pickups don't require shipments to be manifested first — just declare the box count. Confirmation arrives via WhatsApp.
                </p>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Pickup Location *</label>
              <select value={locationId} onChange={e => setLocationId(e.target.value)}
                className="w-full border rounded px-2 py-2 text-sm mt-1" disabled={!selectedId}>
                <option value="">{selectedId ? "Select location..." : "Select customer/partner first"}</option>
                {locations.map(l => (
                  <option key={l._id || l.id} value={l._id || l.id}>{l.name || l.location_name} — {l.pincode}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Pickup Date *</label>
                <Input type="date" value={pickupDate} onChange={e => setPickupDate(e.target.value)} className="mt-1" min={todayIso()} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Slot *</label>
                <select value={pickupSlot} onChange={e => setPickupSlot(e.target.value)}
                  className="w-full border rounded px-2 py-2 text-sm mt-1">
                  <option value="MORNING">Morning</option>
                  <option value="EVENING">Evening</option>
                  <option value="URGENT">Urgent</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Contact Person *</label>
                <Input value={contactPerson} onChange={e => setContactPerson(e.target.value)} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Contact Phone *</label>
                <Input value={contactPhone} onChange={e => setContactPhone(e.target.value)} className="mt-1" />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Box Count *</label>
              <Input type="number" min="1" value={boxCount} onChange={e => setBoxCount(e.target.value)} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving ? "Creating..." : "Create Pickup Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Card({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="text-xs text-slate-500 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function D({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-800">{value}</div>
    </div>
  );
}
